/** What the operations views share: one read-and-draw mount, panels, tables and unit formatting. */
import type { Reader } from "../contracts/decode.ts";
import type { ReadEnvelope } from "../contracts/envelope.ts";
import { renderFreshnessBar } from "../app/components.ts";
import { h, setChildren, type Child } from "../app/dom.ts";
import { formatQuantity, parseInstant } from "../app/format.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../app/state.ts";
import type { ViewContext, ViewHandle } from "../app/view.ts";
import { handleOf, makeFrame } from "./frame.ts";
import "../styles/ops.css";

/** The most rows an operations view keeps in memory across pages. */
export const ROWS_IN_MEMORY_MAX = 2000;

const MIB = 1_048_576n;

export type Tone = "ok" | "warn" | "bad" | "info";

/** A titled panel (a labelled region). */
export function panel(title: string, id: string, ...content: Child[]): HTMLElement {
  return h(
    "section",
    { class: "panel", attrs: { "aria-labelledby": id } },
    h("h2", { attrs: { id } }, title),
    ...content,
  );
}

/** A plain data table with column headers; an empty table says so instead of showing nothing. */
export function simpleTable(
  label: string,
  headers: readonly string[],
  rows: readonly (readonly Child[])[],
): HTMLElement {
  return h(
    "div",
    { class: "table-wrap" },
    h(
      "table",
      { class: "data-table", attrs: { "aria-label": label } },
      h(
        "thead",
        {},
        h("tr", {}, ...headers.map((header) => h("th", { attrs: { scope: "col" } }, header))),
      ),
      h(
        "tbody",
        {},
        ...(rows.length === 0
          ? [
              h(
                "tr",
                {},
                h("td", { attrs: { colspan: String(headers.length) } }, "Nothing reported."),
              ),
            ]
          : rows.map((cells) => h("tr", {}, ...cells.map((cell) => h("td", {}, cell))))),
      ),
    ),
  );
}

/** Microseconds from one UTC instant to a later one (a negative figure is never reported). */
export function lagUs(from: string, to: string): number {
  const micros = parseInstant(to) - parseInstant(from);
  return micros < 0n ? 0 : Number(micros);
}

/** Format bytes as MiB with one decimal, by integer arithmetic. */
export function formatMiB(bytes: number): string {
  const tenths = (BigInt(bytes) * 10n) / MIB;
  return `${formatQuantity(tenths / 10n)}.${String(tenths % 10n)} MiB`;
}

/** Slice of a digest or commit that is long enough to compare and short enough to read. */
export function shortHash(hash: string): string {
  return hash.slice(0, 12);
}

/** Show a source link only if it is `https:`; the label is set as text. */
export function sourceLink(url: string, label: string): HTMLElement {
  return h(
    "a",
    { attrs: { href: url, rel: "noopener noreferrer", target: "_blank" } },
    `${label} (opens the source)`,
  );
}

export interface OpsSpec<R> {
  readonly id: string;
  readonly title: string;
  readonly projection: string;
  readonly row: Reader<R>;
  /** Draw the rows read so far (all pages) as the view's body; `latest` is the last envelope read. */
  readonly draw: (
    rows: readonly R[],
    ctx: ViewContext,
    latest: ReadEnvelope<R>,
  ) => readonly Child[];
  /** Called once with the body, for panels that read their own projection. */
  readonly extra?: (ctx: ViewContext, body: HTMLElement) => void;
}

/** Show an envelope's freshness bar and its state (stale, unavailable or empty) in the frame. */
export function showEnvelope(
  frame: { readonly banner: HTMLElement; readonly freshness: HTMLElement },
  envelope: ReadEnvelope<unknown>,
  streamSequence: number | null,
): void {
  const state = stateOfEnvelope(envelope);
  setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
  setChildren(frame.freshness, [renderFreshnessBar(envelope, { streamSequence })]);
}

/**
 * Mount a view that reads one projection (paged), keeps what it reads bounded and draws it.
 * Operations views are workspace-scoped (P6 open items ruling): the read sends no `account_id`.
 */
export function mountOps<R>(ctx: ViewContext, spec: OpsSpec<R>): ViewHandle {
  const frame = makeFrame(spec.id, spec.title);
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const body = h("div", { class: "ops-body" });
  const more = h("button", { attrs: { type: "button", hidden: "" } }, "Load more rows");
  setChildren(frame.body, [body, more]);
  let loaded: readonly R[] = [];
  let cursor: string | null = null;
  const load = (next: string | null): void => {
    void ctx.client
      .read(spec.projection, spec.row, next === null ? {} : { cursor: next })
      .then((answer) => {
        if (answer === null || ctx.scope.disposed) {
          return;
        }
        loaded = [...loaded, ...answer.page.rows].slice(0, ROWS_IN_MEMORY_MAX);
        cursor = answer.page.hasMore ? answer.page.nextCursor : null;
        more.hidden = cursor === null;
        showEnvelope(frame, answer, ctx.streamSequence.get());
        setChildren(body, spec.draw(loaded, ctx, answer));
      })
      .catch((error: unknown) => {
        setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
      });
  };
  more.addEventListener("click", () => {
    load(cursor);
  });
  load(null);
  spec.extra?.(ctx, frame.body);
  return handleOf(frame, ctx);
}

/** A landing page: a heading and the links to the views under it. */
export function mountLanding(
  ctx: ViewContext,
  id: string,
  title: string,
  links: readonly (readonly [string, string, string])[],
): ViewHandle {
  const frame = makeFrame(id, title);
  frame.body.append(
    h(
      "ul",
      {},
      ...links.map(([href, label, note]) =>
        h("li", {}, h("a", { attrs: { href } }, label), ` — ${note}`),
      ),
    ),
  );
  return handleOf(frame, ctx);
}

/** Tone of a health state: only `OK` is green; unknown is never shown as healthy. */
export function healthTone(state: "OK" | "DEGRADED" | "DOWN" | "UNKNOWN"): Tone {
  switch (state) {
    case "OK":
      return "ok";
    case "DEGRADED":
      return "warn";
    case "DOWN":
      return "bad";
    case "UNKNOWN":
      return "info";
  }
}

/** Format a signed offset in microseconds as whole milliseconds. */
export function formatMs(us: number): string {
  return `${String(Math.trunc(us / 1000))} ms`;
}

/** Group rows by their `kind`. */
export function byKindOf<R extends { readonly kind: string }, K extends R["kind"]>(
  rows: readonly R[],
  kind: K,
): Extract<R, { kind: K }>[] {
  return rows.filter((row): row is Extract<R, { kind: K }> => row.kind === kind);
}
